// src/pages/dashboard/Settings/AllowanceTypesManagement.js
// UPDATED WITH CALCULATION_METHOD SELECTOR

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  message,
  Popconfirm,
  Typography,
  Tooltip,
  Alert,
  Select,
  Radio
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  PercentageOutlined,
  InfoCircleOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { supabase } from '../../../services/supabase';
import allowanceService from '../../../services/allowanceService';
import { SectionHead, StatusPill } from '../../../components/ui';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const AllowanceTypesManagement = ({ companyId }) => {
  const [allowanceTypes, setAllowanceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [form] = Form.useForm();
  const [calculationMethod, setCalculationMethod] = useState('fixed');

  useEffect(() => {
    if (companyId) {
      fetchAllowanceTypes();
    }
  }, [companyId]);

  const fetchAllowanceTypes = async () => {
    try {
      setLoading(true);
      const types = await allowanceService.getAllowanceTypes(companyId);
      setAllowanceTypes(types);
    } catch (error) {
      message.error('Failed to fetch allowance types');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingType(null);
    form.resetFields();
    setCalculationMethod('fixed');
    setModalVisible(true);
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setCalculationMethod(type.calculation_method || 'fixed');
    form.setFieldsValue({
      name: type.name,
      description: type.description,
      calculation_method: type.calculation_method || 'fixed',
      is_percentage: type.is_percentage,
      is_taxable: type.is_taxable,
      is_active: type.is_active
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Set is_percentage based on calculation_method
      const isPercentage = values.calculation_method === 'percentage';

      const allowanceTypeData = {
        ...values,
        is_percentage: isPercentage,
        company_id: companyId
      };

      if (editingType) {
        await allowanceService.updateAllowanceType(editingType.id, allowanceTypeData);
        message.success('Allowance type updated successfully');
      } else {
        await allowanceService.createAllowanceType(allowanceTypeData);
        message.success('Allowance type created successfully');
      }

      setModalVisible(false);
      fetchAllowanceTypes();
    } catch (error) {
      message.error(editingType ? 'Failed to update allowance type' : 'Failed to create allowance type');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (typeId) => {
    try {
      setLoading(true);
      await allowanceService.deleteAllowanceType(typeId);
      message.success('Allowance type deleted successfully');
      fetchAllowanceTypes();
    } catch (error) {
      message.error('Failed to delete allowance type');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDefaults = async () => {
    try {
      setLoading(true);
      await allowanceService.createDefaultAllowanceTypes(companyId);
      message.success('Default allowance types created successfully');
      fetchAllowanceTypes();
    } catch (error) {
      message.error('Failed to create default allowance types');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getCalculationMethodTag = (method) => {
    switch (method) {
      case 'percentage':
        return <StatusPill tone="primary">Percentage</StatusPill>;
      case 'per_day':
        return <StatusPill tone="positive">Per day</StatusPill>;
      case 'fixed':
      default:
        return <StatusPill tone="neutral">Fixed amount</StatusPill>;
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Text strong>{text}</Text>
          {getCalculationMethodTag(record.calculation_method)}
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Text type="secondary">{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Tax Status',
      key: 'tax_status',
      width: 120,
      render: (_, record) => (
        <StatusPill tone={record.is_taxable ? 'warning' : 'neutral'}>
          {record.is_taxable ? 'Taxable' : 'Non-taxable'}
        </StatusPill>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <StatusPill tone={record.is_active ? 'positive' : 'neutral'}>
          {record.is_active ? 'Active' : 'Inactive'}
        </StatusPill>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size={2}>
          <Button
            type="text"
            className="icon-action"
            data-tone="primary"
            aria-label="Edit allowance type"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete this allowance type?"
            description="This will deactivate the allowance type. Existing employee allowances will not be affected."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              className="icon-action"
              data-tone="danger"
              aria-label="Delete allowance type"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <SectionHead
        title="Allowance types"
        subtitle="Allowances you can add to an employee's pay: fixed amounts, a share of salary, or an amount per working day."
        actions={
          <>
            {allowanceTypes.length === 0 && (
              <Button onClick={handleCreateDefaults} loading={loading}>
                Create default types
              </Button>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNew}
            >
              Add allowance type
            </Button>
          </>
        }
      />

      {allowanceTypes.length === 0 && (
        <Alert
          message="No Allowance Types Found"
          description="Create allowance types to start managing employee allowances. You can create default types or add custom ones."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        columns={columns}
        dataSource={allowanceTypes}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
      />

      <Modal
        title={editingType ? 'Edit Allowance Type' : 'Add New Allowance Type'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        confirmLoading={loading}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            calculation_method: 'fixed',
            is_percentage: false,
            is_taxable: true,
            is_active: true
          }}
        >
          <Form.Item
            name="name"
            label="Allowance Type Name"
            rules={[
              { required: true, message: 'Please enter allowance type name' },
              { min: 2, message: 'Name must be at least 2 characters' },
              { max: 100, message: 'Name cannot exceed 100 characters' }
            ]}
          >
            <Input placeholder="e.g., Transport Allowance, Mobile Package" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { max: 500, message: 'Description cannot exceed 500 characters' }
            ]}
          >
            <TextArea
              rows={3}
              placeholder="Brief description of this allowance type..."
            />
          </Form.Item>

          {/* NEW: CALCULATION METHOD SELECTOR */}
          <Form.Item
            name="calculation_method"
            label={
              <Space>
                Calculation Method
                <Tooltip title="Choose how this allowance should be calculated">
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
            }
            rules={[{ required: true, message: 'Please select calculation method' }]}
          >
            <Radio.Group onChange={(e) => setCalculationMethod(e.target.value)}>
              <Space direction="vertical">
                <Radio value="fixed">
                  <Space>
                    <DollarOutlined style={{ color: 'var(--theme-muted)' }} />
                    <div>
                      <Text strong>Fixed Amount</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        One-time amount (e.g., Rs 500 per month)
                      </Text>
                    </div>
                  </Space>
                </Radio>
                <Radio value="percentage">
                  <Space>
                    <PercentageOutlined style={{ color: 'var(--theme-primary-ink)' }} />
                    <div>
                      <Text strong>Percentage of Salary</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Calculated as % of base salary (e.g., 10% of salary)
                      </Text>
                    </div>
                  </Space>
                </Radio>
                <Radio value="per_day">
                  <Space>
                    <CalendarOutlined style={{ color: 'var(--theme-positive-ink)' }} />
                    <div>
                      <Text strong>Per Working Day</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Amount × working days (e.g., Rs 50/day × 26 days)
                      </Text>
                    </div>
                  </Space>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="is_taxable"
            label={
              <Space>
                Tax Status
                <Tooltip title="Taxable allowances are included in taxable income calculations. Non-taxable allowances are excluded.">
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
            }
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Taxable"
              unCheckedChildren="Non-Taxable"
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Status"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Active"
              unCheckedChildren="Inactive"
            />
          </Form.Item>
        </Form>

        <Alert
          message="Calculation Examples"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><strong>Fixed:</strong> Transport Allowance = Rs 500 (paid once per period)</li>
              <li><strong>Percentage:</strong> Performance Bonus = 10% of Rs 20,000 salary = Rs 2,000</li>
              <li><strong>Per Day:</strong> Transport = Rs 50/day × 26 working days = Rs 1,300</li>
            </ul>
          }
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Modal>
    </Card>
  );
};

export default AllowanceTypesManagement;